import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  navigateToCanonicalForumThread,
  THREAD_NAV_ORIGIN_COMMUNITY,
} from '../features/posts/canonicalThreadNavigation';
import { MessageCircle, Heart, FileText, Image } from 'lucide-react';

const ActivityItem = ({ activity }) => {
  const navigate = useNavigate();

  const getActivityIcon = () => {
    switch (activity.type) {
      case 'forum_post':
        return <FileText className="text-purple-400" size={20} />;
      case 'forum_comment':
        return <MessageCircle className="text-blue-400" size={20} />;
      case 'fanart_like':
        return <Heart className="text-pink-400" size={20} />;
      case 'fanart_comment':
        return <Image className="text-cyan-400" size={20} />;
      default:
        return <FileText className="text-gray-400" size={20} />;
    }
  };

  const getActivityText = () => {
    switch (activity.type) {
      case 'forum_post':
        // Check if it's a review (title starts with "Review of ")
        const isReview = activity.title?.startsWith('Review of ');
        const displayTitle = isReview 
          ? activity.title.replace('Review of ', '') 
          : activity.title;
        
        return (
          <div>
            <span className="text-white/90 font-medium">
              {isReview ? 'Reviewed: ' : 'Posted a thread: '}
            </span>
            <span className="text-cyan-400" style={isReview ? { fontStyle: 'italic' } : {}}>
              {displayTitle}
            </span>
          </div>
        );
      case 'forum_comment':
        return (
          <div>
            <span className="text-white/90 font-medium">Commented on: </span>
            <span className="text-cyan-400">{activity.post_title}</span>
          </div>
        );
      case 'fanart_like':
        return (
          <div>
            <span className="text-white/90 font-medium">Liked artwork: </span>
            <span className="text-pink-400">{activity.title}</span>
            <span className="text-white/50 text-sm"> by {activity.artist_name}</span>
          </div>
        );
      case 'fanart_comment':
        return (
          <div>
            <span className="text-white/90 font-medium">Commented on artwork: </span>
            <span className="text-cyan-400">{activity.title}</span>
            <span className="text-white/50 text-sm"> by {activity.artist_name}</span>
          </div>
        );
      default:
        return <span className="text-white/70">Activity</span>;
    }
  };

  const handleClick = () => {
    switch (activity.type) {
      case 'forum_post':
      case 'forum_comment':
        navigateToCanonicalForumThread(navigate, activity, {
          origin: THREAD_NAV_ORIGIN_COMMUNITY,
        });
        break;
      case 'fanart_like':
      case 'fanart_comment':
        // Navigate to arthouse artwork detail page
        navigate(`/arthouse/art/${activity.art_id || activity.id}`);
        break;
      default:
        break;
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };
    
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    
    return 'Just now';
  };

  return (
    <div
      onClick={handleClick}
      className="group bg-white/[0.04] hover:bg-white/[0.08] rounded-xl p-4 border border-white/10 hover:border-cyan-400/30 transition-all duration-200 cursor-pointer"
    >
      <div className="flex gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center border border-white/10">
          {getActivityIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm mb-1">
            {getActivityText()}
          </div>
          
          {/* Activity text/excerpt */}
          {activity.text && (
            <p className="text-sm text-white/50 line-clamp-2 mt-2">
              {activity.text}
            </p>
          )}
          
          {/* Fan art thumbnail */}
          {(activity.type === 'fanart_like' || activity.type === 'fanart_comment') && activity.image_url && (
            <div className="mt-3">
              <img
                src={activity.image_url}
                alt={activity.title}
                className="w-full h-32 object-cover rounded-lg border border-white/10"
              />
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-3 mt-3 text-xs text-white/40">
            <span>{timeAgo(activity.created_at)}</span>
            {activity.likes > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Heart size={12} />
                  {activity.likes}
                </span>
              </>
            )}
            {activity.comments > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <MessageCircle size={12} />
                  {activity.comments}
                </span>
              </>
            )}
            {activity.shares > 0 && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  {activity.shares} {activity.shares === 1 ? 'share' : 'shares'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityItem;
